import React from 'react';
import styled from 'styled-components';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  flex-wrap: wrap;
  gap: 12px;
`;

const PaginationWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
`;

const PageButton = styled.button`
  min-width: 36px;
  height: 36px;
  padding: 0 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => (props.active ? props.theme.colors.primary : props.theme.colors.surface)};
  color: ${(props) => (props.active ? '#fff' : props.theme.colors.textPrimary)};
  border: 1px solid ${(props) => (props.active ? props.theme.colors.primary : props.theme.colors.inputGray + '44')};
  border-radius: 8px;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  font-size: 14px;
  font-weight: ${(props) => (props.active ? '600' : '400')};
  opacity: ${(props) => (props.disabled ? 0.4 : 1)};
  transition: all 0.15s;

  &:hover:not(:disabled) {
    background-color: ${(props) => (props.active ? props.theme.colors.primary : props.theme.colors.primary + '18')};
    border-color: ${(props) => props.theme.colors.primary};
  }
`;

const Ellipsis = styled.span`
  min-width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.theme.colors.textSecondary};
  font-size: 14px;
`;

const PageInfo = styled.span`
  font-size: 13px;
  color: ${(props) => props.theme.colors.textSecondary};
  margin: 0 8px;
`;

const PerPageWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PerPageLabel = styled.span`
  font-size: 13px;
  color: ${(props) => props.theme.colors.textSecondary};
`;

const PerPageSelect = styled.select`
  height: 36px;
  padding: 0 10px;
  font-size: 13px;
  border: 1px solid ${(props) => props.theme.colors.inputGray + '44'};
  border-radius: 8px;
  background-color: ${(props) => props.theme.colors.surface};
  color: ${(props) => props.theme.colors.textPrimary};
  cursor: pointer;
  outline: none;
  transition: all 0.15s;

  &:hover, &:focus {
    border-color: ${(props) => props.theme.colors.primary};
  }
`;

const Pagination = ({ itemsPerPage, totalItems, paginate, currentPage, itemsPerPageOptions, onItemsPerPageChange }) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  if (totalPages <= 1 && !itemsPerPageOptions) return null;

  // Build page numbers with ellipsis
  const getPageNumbers = () => {
    const pages = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    // Always show first page
    pages.push(1);

    if (currentPage > 3) {
      pages.push('...');
    }

    // Pages around current
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (currentPage < totalPages - 2) {
      pages.push('...');
    }

    // Always show last page
    pages.push(totalPages);

    return pages;
  };

  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <PaginationContainer>
      {itemsPerPageOptions && onItemsPerPageChange ? (
        <PerPageWrapper>
          <PerPageLabel>Show</PerPageLabel>
          <PerPageSelect
            value={itemsPerPage}
            onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
          >
            {itemsPerPageOptions.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </PerPageSelect>
          <PerPageLabel>per page</PerPageLabel>
        </PerPageWrapper>
      ) : <div />}

      <PaginationWrapper>
        {totalPages > 1 && (
          <>
            <PageButton
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <FaChevronLeft size={12} />
            </PageButton>

            {getPageNumbers().map((page, index) =>
              page === '...' ? (
                <Ellipsis key={`ellipsis-${index}`}>...</Ellipsis>
              ) : (
                <PageButton
                  key={page}
                  onClick={() => paginate(page)}
                  active={currentPage === page}
                >
                  {page}
                </PageButton>
              )
            )}

            <PageButton
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <FaChevronRight size={12} />
            </PageButton>
          </>
        )}

        <PageInfo>{startItem}-{endItem} of {totalItems}</PageInfo>
      </PaginationWrapper>
    </PaginationContainer>
  );
};

export default Pagination;
